import React from 'react'
import TemplateInput from '@/components/ui/TemplateInput'
import type { WorkflowNodeData } from '@/types/workflow'

interface CreateTaskConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, infoBannerClass } from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const TASK_TYPE_OPTIONS = [
  { value: 'call', label: 'Call' },
  { value: 'linkedin_connect', label: 'LinkedIn Connect' },
  { value: 'linkedin_message', label: 'LinkedIn Message' },
  { value: 'manual_email', label: 'Manual Email' },
  { value: 'research', label: 'Research' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'book_meeting', label: 'Book Meeting' },
  { value: 'custom', label: 'Custom' },
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

const CreateTaskConfig: React.FC<CreateTaskConfigProps> = ({ data, updateField }) => {
  const config = data.create_task_config || { task_type: 'follow_up', title_template: '', description_template: '', priority: 'medium', due_in_days: 1 }
  const updateConfig = (key: string, value: any) => {
    updateField('create_task_config', { ...config, [key]: value })
  }

  const isBookMeeting = (config.task_type || 'follow_up') === 'book_meeting'
  const titlePlaceholder = isBookMeeting
    ? 'Book a meeting with {{contact.full_name}} (type {{ for variables)'
    : 'Follow up with {{contact.full_name}} (type {{ for variables)'
  const descriptionPlaceholder = isBookMeeting
    ? 'Reach out to {{contact.full_name}} at {{contact.company_name}}, confirm their availability, and schedule the meeting. (type {{ for variables)'
    : 'Contact at {{contact.company_name}}... (type {{ for variables)'

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Create Task"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <Field label="Task Type">
        {(f) => (
          <Select
            {...f}
            value={config.task_type || 'follow_up'}
            onChange={(value) => updateConfig('task_type', value)}
            options={TASK_TYPE_OPTIONS}
            className="w-full"
          />
        )}
      </Field>

      {/* The required-error line is Field's `error`, not a sibling — that wires it
          to the control via `aria-describedby` + `aria-invalid`, which a loose
          `<p>` never did. */}
      <Field
        label="Title Template"
        required
        error={
          !(config.title_template || '').trim()
            ? "Title template is required. The workflow can't be activated without it."
            : undefined
        }
      >
        {(f) => (
          <TemplateInput
            {...f}
            value={config.title_template || ''}
            onChange={(v) => updateConfig('title_template', v)}
            placeholder={titlePlaceholder}
            multiline={false}
          />
        )}
      </Field>

      <Field label="Description Template">
        {(f) => (
          <TemplateInput
            {...f}
            value={config.description_template || ''}
            onChange={(v) => updateConfig('description_template', v)}
            placeholder={descriptionPlaceholder}
            rows={3}
          />
        )}
      </Field>

      <Field label="Priority">
        {(f) => (
          <Select
            {...f}
            value={config.priority || 'medium'}
            onChange={(value) => updateConfig('priority', value)}
            options={PRIORITY_OPTIONS}
            className="w-full"
          />
        )}
      </Field>

      {/* Book Meeting tasks have no preset deadline: the due date stays empty
          until the rep books the meeting, then follows the scheduled meeting
          time automatically (AB#21858). */}
      {!isBookMeeting && (
        <Field label="Due In (Days)">
          {(f) => (
            <input
              {...f}
              type="number"
              value={config.due_in_days ?? 1}
              onChange={(e) => updateConfig('due_in_days', parseInt(e.target.value) || 0)}
              min={0}
              placeholder="Days until due"
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      <div className={infoBannerClass}>
        <p className="text-xs">
          {isBookMeeting
            ? 'Creates a “Book Meeting” task assigned to a sales rep. From the Tasks page they can book the meeting with the contact (Outlook, Zoom, or Calendly) and send the invite. The task has no due date until the meeting is booked — it then follows the scheduled meeting time automatically.'
            : 'Creates a task for your sales team to complete manually.'}
        </p>
      </div>
    </div>
  )
}

export default CreateTaskConfig
